import React, { useCallback, useMemo, useState } from "react";
import { useTheme } from "styled-components";
import {
  FiAward, FiCalendar, FiChevronRight, FiDollarSign, FiFileText, FiGrid, FiInfo, FiLayers, FiSearch, FiX,
} from "react-icons/fi";
import {
  Empty, Group, IconTile, SearchField, Section, Widget, WidgetGrid, tokens,
} from "../activity-logs/styles";
import { SegmentedControl } from "../activity-logs/components/primitives";
import { IOS } from "../activity-logs/labels";
import {
  CompositionWidget, CreditsPage, DirectoryCard, HeaderLink, Notice, SpanCell, StepNumber, StorageBar, SupplierGrid, Tag, TaxBar,
} from "./styles";
import {
  TAXES, categoryMeta, displayName, formatCNPJ, formatCount, formatCurrency, formatMonth, formatPercent, taxMeta,
} from "./dashboardData";
import { formatRate } from "./detailData";
import { ABC, CHART_COLORS, buildSupplierStats } from "./supplierStats";
import { SupplierBarList } from "./components/supplierCharts";
import { BackButton, MoneyValue, RankRows, SupplierAvatar, ScrolledTopBar } from "./components/parts";
import { ListRow } from "./components/TopLists";
import SupplierRecords from "./components/SupplierRecords";
import FornecedorDetailScreen from "./FornecedorDetailScreen";
import useSupplierItems from "./useSupplierItems";

/**
 * Dashboard de um fornecedor no projeto: quanto ele gera, em quais créditos e onde
 * está no conjunto. Usa só o que a tela principal já trouxe de /creditos/agrupado
 * (um registro por tipo de crédito), sem novas chamadas. Os itens e documentos
 * ficam no nível seguinte, ao abrir um tipo (FornecedorDetailScreen).
 */

// Soma por chave a partir dos tipos de crédito do fornecedor
const rankTipos = (tipos, keyOf) => {
  const groups = {};
  tipos.forEach((t) => {
    const key = keyOf(t);
    if (!key) return;
    const g = groups[key] || (groups[key] = { key, total: 0, count: 0 });
    g.total += t.valor;
    g.count += 1;
  });
  return Object.values(groups).sort((a, b) => b.total - a.total);
};

const buildDetail = (supplier) => {
  const tipos = supplier.tipos || [];
  const base = tipos.reduce((sum, t) => sum + (Number(t.fornecedor.totalBase) || 0), 0);
  const dates = tipos
    .flatMap((t) => [t.fornecedor.periodoInicio, t.fornecedor.periodoFim])
    .map((d) => String(d || "").slice(0, 10))
    .filter(Boolean)
    .sort();
  return {
    base,
    effectiveRate: base > 0 ? (supplier.total / base) * 100 : null,
    perItem: supplier.count > 0 ? supplier.total / supplier.count : 0,
    taxes: TAXES.filter((t) => supplier.byTax[t.key]).map((t) => ({
      ...t,
      total: supplier.byTax[t.key],
      share: supplier.total > 0 ? (supplier.byTax[t.key] / supplier.total) * 100 : 0,
      count: tipos.filter((x) => x.credito.tipo === t.key).length,
    })),
    bars: tipos.map((t) => ({
      id: t.credito.tipoCredito,
      name: t.credito.titulo,
      total: t.valor,
      share: supplier.total > 0 ? (t.valor / supplier.total) * 100 : 0,
      tipo: t,
    })),
    // Um CFOP só entra quando o tipo tem um único; senão o valor iria para vários
    byCfop: rankTipos(tipos, (t) => {
      const cfops = t.fornecedor.cfops || [];
      if (!cfops.length) return "";
      return cfops.length === 1 ? `CFOP ${cfops[0]}` : "Vários CFOPs";
    }),
    byCategory: rankTipos(tipos, (t) => categoryMeta(t.credito.categoria).label),
    byRate: rankTipos(tipos, (t) => (t.fornecedor.mediaAliquota || t.fornecedor.mediaAliquota === 0 ? formatRate(t.fornecedor.mediaAliquota) : "")),
    // Atalhos para a revisão: onde está o volume e onde está o valor por item
    mostItems: [...tipos].sort((a, b) => b.quantidade - a.quantidade)[0] || null,
    bestPerItem: [...tipos].filter((t) => t.quantidade > 0).sort((a, b) => b.valor / b.quantidade - a.valor / a.quantidade)[0] || null,
    firstDate: dates[0] || "",
    lastDate: dates[dates.length - 1] || "",
  };
};

// Até 3 valores por atributo, com a parte do crédito; com um só valor vira um fato, não um ranking
function ProfileBlock({ title, rows, total }) {
  const theme = useTheme();
  if (rows.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: "0.74em", color: tokens.label2(theme), marginBottom: 6 }}>{title}</div>
      {rows.slice(0, 3).map((r) => (
        <div key={r.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: "0.86em", padding: "3px 0" }}>
          <span style={{ fontWeight: 500, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }} title={r.key}>{r.key}</span>
          <span style={{ color: tokens.label2(theme), whiteSpace: "nowrap" }}>{total > 0 ? formatPercent((r.total / total) * 100) : ""}</span>
        </div>
      ))}
      {rows.length > 3 && <div style={{ fontSize: "0.74em", color: tokens.label2(theme), marginTop: 2 }}>+{rows.length - 3}</div>}
    </div>
  );
}

// Elemento próprio da tela: o trecho da curva ABC que este fornecedor ocupa
function PositionWidget({ me, project, detail }) {
  const theme = useTheme();
  const cls = ABC[me.abc] || ABC.C;
  const before = Math.max(0, (me.cumulativeShare || 0) - (me.share || 0));
  const ratio = project.medianPerItem > 0 ? detail.perItem / project.medianPerItem : null;

  return (
    <Widget $delay={250} style={{ height: "100%" }}>
      <div className="w-title">Posição no projeto</div>
      <div className="w-sub">Onde este fornecedor está na curva ABC</div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 16 }}>
        <span style={{ fontSize: "2.2em", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1 }}>{formatCount(me.rank)}º</span>
        <span style={{ fontSize: "0.82em", color: tokens.label2(theme) }}>de {formatCount(project.supplierCount)} fornecedores</span>
        <span style={{ flex: 1 }} />
        <StepNumber $color={cls.color} title={cls.label} style={{ width: 22, height: 22, fontSize: "0.7em" }}>{cls.key}</StepNumber>
      </div>

      {/* Faixas A, B e C pelo peso no crédito; o trecho do fornecedor fica aceso */}
      <div style={{ position: "relative", height: 10, marginTop: 18 }} role="img" aria-label={`Ocupa de ${formatPercent(before)} a ${formatPercent(me.cumulativeShare)} do crédito acumulado`}>
        <div style={{ display: "flex", gap: 2, height: "100%", borderRadius: 4, overflow: "hidden" }}>
          {project.classes.filter((c) => c.share > 0).map((c) => (
            <span key={c.key} style={{ flexGrow: c.share, flexBasis: 0, background: c.color, opacity: 0.18 }} />
          ))}
        </div>
        <span
          style={{
            position: "absolute",
            top: -3,
            bottom: -3,
            left: `${before}%`,
            width: `max(4px, ${me.share}%)`,
            borderRadius: 4,
            background: cls.color,
            boxShadow: `0 0 0 2px ${tokens.solid(theme)}`,
          }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, fontSize: "0.72em", color: tokens.label2(theme) }}>
        <span>maiores</span>
        <span>{formatPercent(me.cumulativeShare)} acumulado</span>
        <span>menores</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 18 }}>
        <div>
          <div style={{ fontSize: "0.74em", color: tokens.label2(theme) }}>Parte do crédito</div>
          <div style={{ fontSize: "1.05em", fontWeight: 600, marginTop: 2 }}>{formatPercent(me.share)}</div>
        </div>
        <div>
          <div style={{ fontSize: "0.74em", color: tokens.label2(theme) }}>Por item × mediana</div>
          <div style={{ fontSize: "1.05em", fontWeight: 600, marginTop: 2 }}>
            {ratio === null ? "—" : `${ratio.toLocaleString("pt-BR", { maximumFractionDigits: ratio >= 10 ? 0 : 1 })}×`}
          </div>
        </div>
      </div>
    </Widget>
  );
}

function Overview({ supplier, me, project, detail, loader, onReachRecords, onOpenTipo, onGoTipos }) {
  const theme = useTheme();
  const tipoCount = supplier.tipos.length;
  const period = detail.firstDate
    ? `${formatMonth(detail.firstDate.slice(0, 7))} — ${formatMonth(detail.lastDate.slice(0, 7))}`
    : "";

  return (
    <>
      <Section $first>
        <WidgetGrid>
          <Widget $delay={0} $clickable onClick={onGoTipos}>
            <div className="w-head"><FiDollarSign style={{ color: IOS.mint, opacity: 1 }} /> Crédito identificado</div>
            <div className="w-value"><MoneyValue value={supplier.total} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between", gap: 10 }}>
              <span>{formatCount(tipoCount)} {tipoCount === 1 ? "tipo de crédito" : "tipos de crédito"}</span>
              {/* Divisão por imposto, nas mesmas cores do restante da tela */}
              <TaxBar style={{ width: 80, height: 4, flexShrink: 0 }}>
                {detail.taxes.map((t) => <span key={t.key} style={{ flexGrow: t.total, background: t.color }} />)}
              </TaxBar>
            </div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head"><FiLayers style={{ color: IOS.indigo, opacity: 1 }} /> Base de cálculo</div>
            <div className="w-value"><MoneyValue value={detail.base} /></div>
            <div className="w-foot">alíquota efetiva {formatRate(detail.effectiveRate)}</div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head"><FiFileText style={{ color: IOS.blue, opacity: 1 }} /> Itens</div>
            <div className="w-value">{formatCount(supplier.count)}</div>
            <div className="w-foot">{formatCurrency(detail.perItem)} por item</div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head"><FiCalendar style={{ color: IOS.purple, opacity: 1 }} /> Período</div>
            <div className="w-value">{detail.lastDate ? formatMonth(detail.lastDate.slice(0, 7)) : "—"}</div>
            <div className="w-foot">{period ? `último documento · desde ${formatMonth(detail.firstDate.slice(0, 7))}` : "sem datas na API"}</div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Widget $span={3} $delay={150} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
              <div className="w-title">Créditos deste fornecedor</div>
              {tipoCount > 6 && <HeaderLink onClick={onGoTipos}>Ver todos <FiChevronRight /></HeaderLink>}
            </div>
            <div className="w-sub" style={{ marginBottom: 12 }}>
              {detail.bars[0]
                ? `${displayName(detail.bars[0].name)} responde por ${formatPercent(detail.bars[0].share)}. Clique para ver documentos e itens.`
                : "Nenhum crédito identificado"}
            </div>
            <SupplierBarList suppliers={detail.bars.slice(0, 6)} colors={CHART_COLORS} onOpen={(row) => onOpenTipo(row.tipo)} />
            {tipoCount > 1 && (
              <div style={{ marginTop: "auto", paddingTop: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
                {[
                  { label: "Mais itens", t: detail.mostItems, v: (t) => `${formatCount(t.quantidade)} itens` },
                  { label: "Maior crédito por item", t: detail.bestPerItem, v: (t) => `${formatCurrency(t.valor / t.quantidade)} por item` },
                ].filter((x) => x.t).map((x) => (
                  <button
                    key={x.label}
                    type="button"
                    onClick={() => onOpenTipo(x.t)}
                    style={{ all: "unset", cursor: "pointer", minWidth: 0, paddingTop: 12, borderTop: `1px solid ${tokens.separator(theme)}` }}
                  >
                    <div style={{ fontSize: "0.74em", color: tokens.label2(theme) }}>{x.label}</div>
                    <div style={{ fontSize: "0.86em", fontWeight: 500, marginTop: 3, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }} title={x.t.credito.titulo}>{x.t.credito.titulo}</div>
                    <div style={{ fontSize: "0.76em", color: tokens.label2(theme), marginTop: 1 }}>{x.v(x.t)}</div>
                  </button>
                ))}
              </div>
            )}
          </Widget>

          <CompositionWidget $delay={200}>
            <div className="w-title">Por imposto</div>
            <div className="w-sub">Como o crédito se divide</div>
            <StorageBar role="img" aria-label="Participação de cada imposto no crédito" style={{ marginTop: 14 }}>
              {detail.taxes.map((t) => (
                <span key={t.key} title={`${t.label}: ${formatPercent(t.share)}`} style={{ flexGrow: Math.max(t.share, 0.5), background: t.color }} />
              ))}
            </StorageBar>
            <div style={{ marginTop: 8 }}>
              {detail.taxes.map((t) => (
                <ListRow
                  key={t.key}
                  lead={<span style={{ width: 8, height: 8, borderRadius: "50%", background: t.color, display: "block" }} />}
                  title={t.label}
                  subtitle={`${formatCount(t.count)} ${t.count === 1 ? "tipo" : "tipos"}`}
                  value={t.total}
                  share={t.share}
                  compact
                  onClick={onGoTipos}
                />
              ))}
            </div>
          </CompositionWidget>
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <SpanCell $span={2}>
            <PositionWidget me={me} project={project} detail={detail} />
          </SpanCell>
          <SpanCell $span={2}>
            <Widget $delay={300} style={{ height: "100%" }}>
              <div className="w-title">Perfil</div>
              <div className="w-sub">Natureza, origem e alíquota do crédito</div>
              {/* Com vários tipos o ranking tem barras; com poucos, os fatos bastam */}
              {detail.byCategory.length > 3 ? (
                <div style={{ marginTop: 16 }}><RankRows rows={detail.byCategory} color={IOS.teal} limit={5} /></div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 18, marginTop: 18 }}>
                  <ProfileBlock title="Categoria" rows={detail.byCategory} total={supplier.total} />
                  <ProfileBlock title="CFOP" rows={detail.byCfop} total={supplier.total} />
                  <ProfileBlock title="Alíquota média" rows={detail.byRate} total={supplier.total} />
                </div>
              )}
              {detail.byCategory.length > 3 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 18, marginTop: 18 }}>
                  <ProfileBlock title="CFOP" rows={detail.byCfop} total={supplier.total} />
                  <ProfileBlock title="Alíquota média" rows={detail.byRate} total={supplier.total} />
                </div>
              )}
              {detail.byCfop.length === 0 && detail.byCategory.length === 0 && (
                <div className="w-sub" style={{ marginTop: 14, color: tokens.label2(theme) }}>Sem categoria ou CFOP na API</div>
              )}
            </Widget>
          </SpanCell>
        </WidgetGrid>
      </Section>

      <SupplierRecords supplier={supplier} loader={loader} onReach={onReachRecords} />
    </>
  );
}

// Aba com todos os tipos, em cards do mesmo diretório da aba Fornecedores
function TipoGrid({ supplier, onOpen }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return supplier.tipos.filter((t) => !term
      || [t.credito.titulo, categoryMeta(t.credito.categoria).label, t.credito.tipo, ...(t.fornecedor.cfops || [])]
        .some((v) => String(v || "").toLowerCase().includes(term)));
  }, [supplier.tipos, q]);

  return (
    <Section $first>
      <SearchField $max="420px">
        <FiSearch />
        <input placeholder="Buscar por crédito, categoria, imposto ou CFOP" aria-label="Buscar tipo de crédito" value={q} onChange={(e) => setQ(e.target.value)} />
        {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
      </SearchField>

      {filtered.length === 0 ? (
        <div style={{ marginTop: 16 }}>
          <Group>
            <Empty>
              <div className="e-icon"><FiSearch /></div>
              <div className="e-title">Nenhum crédito encontrado</div>
              {q && <div className="e-sub">Nada encontrado para &quot;{q}&quot;.</div>}
            </Empty>
          </Group>
        </div>
      ) : (
        <SupplierGrid style={{ marginTop: 16 }}>
          {filtered.map((t, idx) => {
            const tax = taxMeta(t.credito.tipo);
            const cat = categoryMeta(t.credito.categoria);
            const Icon = cat.icon;
            const f = t.fornecedor;
            const cfops = f.cfops || [];
            return (
              <DirectoryCard key={t.credito.tipoCredito} type="button" $delay={Math.min(idx, 11) * 25} onClick={() => onOpen(t)}>
                <span className="head">
                  <IconTile $size={40} $color={tax.color}><Icon /></IconTile>
                  <span className="who">
                    <span className="name" title={t.credito.titulo}>{t.credito.titulo}</span>
                    <span className="id">{cat.label}</span>
                  </span>
                </span>
                <span className="body">
                  <span className="amount"><MoneyValue value={t.valor} /></span>
                  <span className="meta">
                    {formatCount(t.quantidade)} {t.quantidade === 1 ? "item" : "itens"}
                    {supplier.total > 0 ? ` · ${formatPercent((t.valor / supplier.total) * 100)} do fornecedor` : ""}
                  </span>
                  <span className="meta">
                    {[f.totalBase ? `base ${formatCurrency(f.totalBase)}` : "", f.mediaAliquota || f.mediaAliquota === 0 ? `alíquota ${formatRate(f.mediaAliquota)}` : ""].filter(Boolean).join(" · ")}
                  </span>
                  <span className="taxes">
                    <Tag $color={tax.color}>{tax.label}</Tag>
                    {cfops.length > 0 && <Tag>CFOP {cfops.slice(0, 2).join(", ")}{cfops.length > 2 ? ` +${cfops.length - 2}` : ""}</Tag>}
                  </span>
                </span>
              </DirectoryCard>
            );
          })}
        </SupplierGrid>
      )}
    </Section>
  );
}

export default function SupplierDetailScreen({ supplier, allSuppliers = [], periodFiltered, onBack }) {
  const [tab, setTab] = useState("overview");
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [recordsWanted, setRecordsWanted] = useState(false);
  // A carga fica na tela, não na seção: trocar de aba ou abrir um tipo não a perde
  const loader = useSupplierItems(supplier, recordsWanted);
  const reachRecords = useCallback(() => setRecordsWanted(true), []);

  // Posição e classe vêm do conjunto inteiro; quem abre pela Visão geral chega sem elas
  const project = useMemo(() => buildSupplierStats(allSuppliers.length ? allSuppliers : [supplier]), [allSuppliers, supplier]);
  const me = useMemo(() => project.suppliers.find((s) => s.id === supplier.id) || { ...supplier, rank: 1, abc: "A", share: 100, cumulativeShare: 100 }, [project, supplier]);
  const detail = useMemo(() => buildDetail(supplier), [supplier]);
  const name = displayName(supplier.name);

  if (selectedTipo) {
    return (
      <FornecedorDetailScreen
        credito={selectedTipo.credito}
        fornecedor={selectedTipo.fornecedor}
        backLabel={name}
        periodFiltered={periodFiltered}
        onBack={() => setSelectedTipo(null)}
      />
    );
  }

  const cls = ABC[me.abc];

  return (
    <CreditsPage>
      <ScrolledTopBar scrollToTop={true} deps={[!!selectedTipo]}>
        <div className="title-row">
          <div style={{ minWidth: 0, display: "flex", alignItems: "flex-end", gap: 14 }}>
            <SupplierAvatar name={supplier.name} size={52} />
            <div style={{ minWidth: 0 }}>
              <BackButton onClick={onBack}>Créditos</BackButton>
              <h1 title={supplier.name}>{name}</h1>
              <div className="meta" style={{ flexWrap: "wrap" }}>
                {cls && <Tag $color={cls.color} style={{ fontSize: "1em" }}>{cls.label}</Tag>}
                {supplier.cnpj && <span className="update-tag">{formatCNPJ(supplier.cnpj)}</span>}
                {supplier.uf && <span className="update-tag">{supplier.uf}</span>}
                <span className="update-tag"><FiAward style={{ verticalAlign: "-2px" }} /> {formatCount(me.rank)}º em crédito</span>
              </div>
            </div>
          </div>
        </div>
        <div className="nav-row">
          <SegmentedControl
            value={tab}
            onChange={setTab}
            options={[
              { key: "overview", icon: <FiGrid />, label: "Visão geral" },
              { key: "tipos", icon: <FiLayers />, label: "Tipos de crédito", count: supplier.tipos.length },
            ]}
          />
        </div>
      </ScrolledTopBar>

      {periodFiltered && (
        <Notice style={{ marginTop: 0, marginBottom: 12 }}>
          <FiInfo />
          <span>Os valores deste fornecedor são de todo o projeto: a API não aplica o filtro de período aqui.</span>
        </Notice>
      )}

      {tab === "tipos"
        ? <TipoGrid supplier={supplier} onOpen={setSelectedTipo} />
        : (
          <Overview
            supplier={supplier}
            me={me}
            project={project}
            detail={detail}
            loader={loader}
            onReachRecords={reachRecords}
            onOpenTipo={setSelectedTipo}
            onGoTipos={() => setTab("tipos")}
          />
        )}
    </CreditsPage>
  );
}
